import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ShieldCheck, Repeat, Zap,
  Boxes, ShieldAlert, Building2, Compass, Wallet, CheckCircle2, ArrowRight,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

/* bold-parser: renders **phrases** as semibold */
const B = ({ t, cls = "text-ink font-semibold" }) => (
  <>
    {t.split(/(\*\*[^*]+\*\*)/g).map((s, i) =>
      s.startsWith("**") && s.endsWith("**")
        ? <strong key={i} className={cls}>{s.slice(2, -2)}</strong>
        : <span key={i}>{s}</span>
    )}
  </>
);

const PILLARS = [
  { icon: ShieldCheck, t: "Durability", d: "Won't crack under heavy tracking. UV & chemical resistant." },
  { icon: Repeat, t: "High Utilization", d: "Dual-sided tread. Non-porous, zero-contamination. Any terrain, instantly." },
  { icon: Zap, t: "Rapid Install", d: "Simple locking system. Machine-set with standard skid steers & telehandlers." },
];

const PERSONAS = [
  {
    icon: Boxes,
    role: "Equipment / Asset Manager",
    segment: "Heavy Civil & Infrastructure",
    pain: "**Idle mats** sitting in the yard. Recurring **cracked-mat replacement** line-items. Painful **logistics** moving mats between jobs.",
    tag: "Utilization + Longevity",
    solution: "Faster turns and **years of service life** keep every mat **working, not stored** — and moving job-to-job **without breakage.**",
    outcomes: ["Maximum asset ROI", "Zero replacement line-items"],
  },
  {
    icon: ShieldAlert,
    role: "Safety Director / HSE Manager",
    segment: "Industrial & Energy",
    pain: "Crews logging **hours in live-hazard zones** — near **pipelines & high-voltage** — assembling **complex, multi-piece** locking systems.",
    tag: "Rapid Install",
    solution: "**Fewer pieces, one simple lock.** Machine-set in minutes gets **crews out of the red zone fast** — no field engineering.",
    outcomes: ["Fewer ground-crew exposure hours", "Zero field trip hazards"],
  },
  {
    icon: Building2,
    role: "General Superintendent",
    segment: "Mega-Project Commercial GC",
    pain: "**Tight urban footprints.** Expensive **night street-closure windows.** Heavy truck traffic **rutting the staging floor.**",
    tag: "Rapid Install + Durability",
    solution: "**Fast lay-down and lift-up** beats the reopening clock; a **tire-twist-proof** surface holds under relentless loads.",
    outcomes: ["Zero municipal fines", "Flawless, clean staging footprint"],
  },
  {
    icon: Compass,
    role: "Lead Civil Engineer",
    segment: "EPCM Firms",
    pain: "**Subgrade failure.** Machinery **sinking.** **Remediation fines** from porous mats **leaching chemicals** into sensitive soils.",
    tag: "Durability + Non-Porous",
    solution: "**High-PSI load capacity** spreads the weight; a **non-porous** surface **washes down clean** — nothing leaches.",
    outcomes: ["Bulletproof structural integrity", "Guaranteed environmental compliance"],
  },
  {
    icon: Wallet,
    role: "Director of Procurement",
    segment: "EPCM & Enterprise Contractors",
    pain: "**Ballooning CapEx.** High **disposal fees** for broken composites. Buying **volumes of single-use** assets.",
    tag: "Lifecycle Optimization",
    solution: "Mats that **turn faster and last for years** shrink the **inventory footprint** you have to buy in the first place.",
    outcomes: ["Slashed procurement budget", "Expense → long-term capital optimization"],
  },
];

export default function ValueMatrix() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Value Matrix · One-Page Brief</span>
            <a href="/big-bad-mats-value-matrix.pdf" download data-testid="download-matrix-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-matrix-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 LANDSCAPE SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="matrix-brief-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="border-b-2 border-ink pb-3">
            <div className="flex items-start justify-between gap-6">
              <div className="max-w-[62%]">
                <div className="flex items-center gap-2 mb-1.5">
                  <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                  <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                  <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Big Bad Mats · Value Proposition Matrix</span>
                </div>
                <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[30px]">
                  Cut Total Cost of Ownership. Kill the Mobilization Bottleneck.
                </h1>
                <p className="mt-2 text-charcoal leading-snug text-[11px]">
                  One temporary construction mat, engineered on three pillars — <span className="font-semibold text-ink">Durability. High Utilization. Rapid Install.</span>
                </p>
              </div>
              {/* pillars */}
              <div className="w-[36%] space-y-1.5">
                {PILLARS.map((p) => (
                  <div key={p.t} className="flex items-start gap-2 border border-border px-2.5 py-1.5">
                    <p.icon className="h-4 w-4 text-flame flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-display font-bold text-ink text-[11px]">{p.t}</span>
                      <span className="text-muted-foreground text-[9px]"> — {p.d}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </header>

          {/* matrix */}
          <div className="flex-1 flex flex-col min-h-0 pt-3">
            {/* column headers */}
            <div className="grid grid-cols-[1.15fr_1.5fr_1.5fr_1.15fr] bg-ink text-white text-[8px] uppercase tracking-[0.16em]">
              <div className="px-3 py-2">Target Role</div>
              <div className="px-3 py-2 border-l border-white/10">Critical Site Pain Point</div>
              <div className="px-3 py-2 border-l border-white/10">The Big Bad Mats Solution</div>
              <div className="px-3 py-2 border-l border-white/10 text-flame">Business Outcome</div>
            </div>
            {/* persona rows */}
            <div className="flex-1 grid grid-rows-5 border-x border-b border-border min-h-0">
              {PERSONAS.map((p, i) => (
                <div key={p.role} data-testid={`persona-${i + 1}`}
                  className={`grid grid-cols-[1.15fr_1.5fr_1.5fr_1.15fr] ${i % 2 ? "bg-muted/40" : "bg-white"} border-t border-border first:border-t-0`}>
                  {/* role */}
                  <div className="px-3 py-2 border-l-[3px] border-flame flex items-start gap-2">
                    <p.icon className="h-5 w-5 text-ink flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-display font-bold text-ink text-[12px] leading-tight">{p.role}</div>
                      <div className="text-[8px] uppercase tracking-wider text-muted-foreground mt-0.5">{p.segment}</div>
                    </div>
                  </div>
                  {/* pain */}
                  <div className="px-3 py-2 border-l border-border text-charcoal text-[10px] leading-snug flex items-center">
                    <p><B t={p.pain} cls="text-ink font-semibold" /></p>
                  </div>
                  {/* solution */}
                  <div className="px-3 py-2 border-l border-border text-charcoal text-[10px] leading-snug flex flex-col justify-center">
                    <span className="inline-flex self-start items-center bg-flame/10 text-flame border border-flame/30 px-1.5 py-0.5 text-[7.5px] uppercase tracking-wider font-semibold mb-1">{p.tag}</span>
                    <p><B t={p.solution} cls="text-ink font-semibold" /></p>
                  </div>
                  {/* outcome */}
                  <div className="px-3 py-2 border-l border-border flex flex-col justify-center gap-1">
                    {p.outcomes.map((o) => (
                      <div key={o} className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-px" />
                        <span className="font-display font-bold text-ink text-[10.5px] leading-tight">{o}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA footer */}
          <div className="mt-3 bg-ink text-white flex items-center justify-between gap-4 px-5 py-3">
            <div>
              <div className="font-display font-extrabold text-[15px] leading-tight">Stop specifying mats. Start engineering your ground plan.</div>
              <div className="text-white/60 text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 print:hidden">
              <Link to="/big-bad-mats-roi" className="inline-flex items-center gap-1.5 bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors">
                Custom Lifecycle ROI <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/big-bad-mats-datasheet" className="inline-flex items-center gap-1.5 border border-white/30 text-white px-4 py-2.5 font-semibold text-[11px] hover:border-flame hover:text-flame transition-colors">
                Technical Data Sheet
              </Link>
            </div>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Positioning &amp; figures are illustrative estimates for evaluation only — not a quote, guarantee, or warranty of any specific result, and not financial, engineering, or legal advice. Actual outcomes vary by application. © {new Date().getFullYear()} {COMPANY.legal}. {COMPANY.tagline}.</p>
        </div>
      </div>
    </div>
  );
}
